import { get_token } from "@/router";

const identity_fields = ["id", "first_name", "last_name", "username", "email"];

function get_nested_value(data, keys) {
    return keys.reduce((result, key) => result || data?.[String(key)], null);
}

function is_plain_object(value) {
    return Boolean(value) && typeof value === "object";
}

function capitalize_key(value) {
    return String(value || "").charAt(0).toUpperCase() + String(value || "").slice(1);
}

export function get_auth_headers(include_json_content_type = true) {
    const auth_token = get_token();
    const headers = {
        "Accept": "application/json"
    };

    if (include_json_content_type) headers["Content-Type"] = "application/json";
    if (auth_token) headers.Authorization = `Token ${auth_token}`;

    return headers;
}

export function extract_collection(response_data, keys = ["data", "results", "users"]) {
    if (Array.isArray(response_data)) return response_data;
    if (!is_plain_object(response_data)) return [];

    return keys.reduce((collection, key) => {
        const value = response_data[String(key)] || response_data[capitalize_key(key)];
        return collection.length ? collection : extract_collection(value, keys);
    }, []);
}

function get_error_object_message(error_data) {
    return Object.entries(error_data)
        .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(", ") : messages}`)
        .join(" | ");
}

export function get_error_message(data, fallback_message = "Unable to complete user request.") {
    const error_data = get_nested_value(data, ["errors", "detail", "error", "message"]) || data;
    const formatters = {
        string: (value) => value,
        object: (value) => Array.isArray(value) ? value.join(", ") : get_error_object_message(value)
    };
    const formatter = error_data ? formatters[typeof error_data] : null;

    return formatter ? formatter(error_data) : fallback_message;
}

export async function get_response_data(response, fallback_message) {
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw new Error(get_error_message(data, fallback_message));
    }

    return data;
}

export function get_role_name(user) {
    return user?.user_role?.role?.name || "";
}

export function get_role_id(role) {
    return role?.id ?? null;
}

export function get_user_role(user) {
    return user?.user_role?.role || user?.role || null;
}

export function get_user_role_id(user) {
    return user?.user_role?.role?.id ?? null;
}

export function get_user_profile_data(user) {
    return user?.profile || user?.user_profile || {};
}

function get_empty_string_value(value) {
    return value == null ? "" : value;
}

function get_identity_data(user) {
    return Object.fromEntries(
        identity_fields.map((field) => [field, get_empty_string_value(user?.[String(field)])])
    );
}

function get_contact_data(user, profile) {
    return {
        phone_number: profile?.phone_number ?? user?.phone_number ?? ""
    };
}

function get_activity_data(user) {
    return {
        date_joined: user?.date_joined || user?.dateJoined || "",
        last_login: user?.last_login || user?.lastLogin || ""
    };
}

function get_row_role_name(user, role) {
    return typeof role === "string" ? role : role?.name || get_role_name(user);
}

function get_row_role_id(user, role) {
    return typeof role === "object" ? role?.id ?? get_user_role_id(user) : get_user_role_id(user);
}

export function get_user_row(user) {
    const role = get_user_role(user);
    const profile = get_user_profile_data(user);

    return {
        ...get_identity_data(user),
        ...get_contact_data(user, profile),
        ...get_activity_data(user),
        role: get_row_role_name(user, role),
        role_id: get_row_role_id(user, role),
        is_active: user?.is_active !== false,
        raw: user
    };
}

function get_url_with_slash(url) {
    return String(url || "").endsWith("/") ? String(url || "") : `${url}/`;
}

function parse_server_user_id(user_id) {
    const user_id_text = String(user_id || "").trim();
    return /^\d+$/.test(user_id_text) ? user_id_text : "";
}

export function get_status_endpoint(base_url, user_id, action) {
    const safe_user_id = parse_server_user_id(user_id);

    return base_url && safe_user_id ? `${get_url_with_slash(base_url)}${safe_user_id}/${action}/` : "";
}

export function build_user_payload(form_payload, role_id, client_app) {
    return {
        first_name: form_payload.first_name,
        last_name: form_payload.last_name,
        email: form_payload.email,
        phone_number: form_payload.phone_number ?? "",
        username: form_payload.username ?? form_payload.email,
        is_active: form_payload.is_active === true,
        client_app,
        ...(role_id ? { role_id } : {})
    };
}

export function get_full_name(user) {
    return [
        `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        user.email,
        "User"
    ].find(Boolean);
}

export function get_initials(user) {
    return [
        `${user.first_name?.charAt(0) || ""}${user.last_name?.charAt(0) || ""}`,
        user.email?.charAt(0),
        "U"
    ].find(Boolean).toUpperCase();
}
